/**
 * PDP "Half-Sizer Upsell" block controller.
 *
 * - Clicking anywhere on the card toggles the checkbox (except the "How does it work?" link,
 *   which opens the Size Chart modal via the theme's <modal-opener>).
 * - Follows variant/size changes: picks the matching Half-Sizer variant from the embedded
 *   size->variant map, updates the "from size X to Y" text + price, and hides the card for
 *   sizes with no Half-Sizer.
 * - Exposes `isChecked` and `currentVariantId` for product-form.js, which adds the Half-Sizer
 *   together with the product on Add to Cart.
 */
if (!customElements.get("half-sizer-upsell")) {
  customElements.define(
    "half-sizer-upsell",
    class HalfSizerUpsell extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.sectionId = this.dataset.sectionId;
        this.sizeIndex = parseInt(this.dataset.sizeIndex, 10);
        try {
          this.map = JSON.parse(this.dataset.variantMap || "{}");
        } catch (e) {
          this.map = {};
        }
        this.currentVariantId = this.dataset.hsCurrentId
          ? Number(this.dataset.hsCurrentId)
          : null;

        this.box = this.querySelector("[data-hs-box]");
        this.checkbox = this.querySelector("[data-hs-checkbox]");
        this.fitEl = this.querySelector("[data-hs-fit]");
        this.priceEl = this.querySelector("[data-hs-price]");

        // Click anywhere on the card toggles, except the "How does it work?" trigger.
        this.box.addEventListener("click", (event) => {
          if (event.target.closest("[data-hs-how], .f-hs-upsell__how")) return;
          this.toggle();
        });
        this.box.addEventListener("keydown", (event) => {
          if (event.target.closest("[data-hs-how], .f-hs-upsell__how")) return;
          if (event.key === " " || event.key === "Enter") {
            event.preventDefault();
            this.toggle();
          }
        });
        const how = this.querySelector("[data-hs-how]");
        if (how) how.addEventListener("click", (event) => event.stopPropagation());

        if (window.FoxThemeEvents && typeof PUB_SUB_EVENTS !== "undefined") {
          this.unsubscribe = window.FoxThemeEvents.subscribe(
            PUB_SUB_EVENTS.variantChange,
            (payload) => {
              const data = payload && payload.data;
              if (!data || String(data.sectionId) !== String(this.sectionId)) return;
              this.onVariantChange(data.variant);
            }
          );
        }
      }

      disconnectedCallback() {
        if (typeof this.unsubscribe === "function") this.unsubscribe();
      }

      get isChecked() {
        return !!(this.checkbox && this.checkbox.checked);
      }

      setChecked(checked) {
        this.checkbox.checked = checked;
        this.classList.toggle("is-checked", checked);
        this.box.setAttribute("aria-pressed", checked ? "true" : "false");
        this.checkbox.dispatchEvent(new Event("change", { bubbles: true }));
      }

      toggle() {
        this.setChecked(!this.checkbox.checked);
      }

      onVariantChange(variant) {
        const size =
          variant?.options?.[this.sizeIndex - 1] ||
          variant?.["option" + this.sizeIndex];
        const entry = this.map[size];

        if (!entry) {
          // No Half-Sizer for this size — hide and clear.
          this.currentVariantId = null;
          if (this.checkbox.checked) this.setChecked(false);
          this.hidden = true;
          return;
        }

        this.currentVariantId = entry.id;
        if (this.fitEl) this.fitEl.textContent = entry.fit;
        if (this.priceEl) this.priceEl.textContent = entry.price;
        this.hidden = false;
      }
    }
  );
}
